import { useEffect, useState, useContext } from "react";
import Axios from "axios";
import Title from "./component/Title";
import GlobalContext from "./GlobalContext";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";

// Fields a player may edit about themselves (mirrors the server whitelist).
const EDITABLE_FIELDS = [
  { name: "phone", label: "Phone Number" },
  { name: "postalCode", label: "Postal Code" },
  { name: "height", label: "Height" },
  { name: "weight", label: "Weight" },
  { name: "position", label: "Position" },
  { name: "work", label: "Work Area" },
  { name: "jersey", label: "Jersey Size" },
  { name: "emName", label: "Emergency Contact Name" },
  { name: "emRelation", label: "Emergency Contact Relationship" },
  { name: "emPhone", label: "Emergency Contact Phone" },
];

function Account() {
  const { globalURL } = useContext(GlobalContext);
  const url = globalURL;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [tab, setTab] = useState(0);

  Axios.defaults.withCredentials = true;

  const authHeader = { headers: { "auth-token": localStorage.getItem("token") } };

  useEffect(() => {
    if (!localStorage.token) {
      navigate("/login");
      return;
    }
    Axios.get(url + "/users/me", authHeader)
      .then((res) => {
        if (res.data.role !== "player") {
          // Staff accounts belong in the admin area.
          navigate("/admin");
          return;
        }
        setAccount(res.data);
      })
      .catch(() => navigate("/login"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    Axios.post(url + "/users/logout", {}, authHeader)
      .then(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh");
        navigate("/login");
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh");
        navigate("/login");
      });
  };

  if (loading) {
    return (
      <div className="container flex justify-center py-20">
        <CircularProgress />
      </div>
    );
  }

  if (!account) return null;

  const linked = Boolean(account.playerID);
  const displayName =
    account.firstname || account.lastname
      ? `${account.firstname || ""} ${account.lastname || ""}`.trim()
      : account.email || account.phone;

  return (
    <div>
      <div className="container">
        <div className="bgRed sm:w-[85%] lg:w-[60%] relative flex justify-center flex-col items-center p-4 border-black border-8 rounded-3xl my-8 sm-shadow">
          <Title word="My Account" />
        </div>
        <div className="content relative my-8 justify-start items-center sm:w-[95%] lg:w-[80%] black-bg border-black border-8">
          <div className="w-full text-start p-4 font-bold text-white">
            Hi, {displayName}
          </div>

          {!linked && (
            <div className="mx-4 mb-4 p-4 border-2 border-yellow-500 rounded text-yellow-300 text-sm">
              Your account isn't linked to your player profile yet. An admin will
              connect it so you can see your stats and history. You can still
              update your contact info below once linked.
            </div>
          )}

          {linked ? (
            <>
              <div className="w-full px-4">
                <Tabs
                  value={tab}
                  onChange={(e, v) => setTab(v)}
                  textColor="inherit"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Profile" />
                  <Tab label="My Stats" />
                  <Tab label="Team & Schedule" />
                </Tabs>
              </div>
              <div className="w-full p-4">
                {tab === 0 && (
                  <ProfileTab
                    url={url}
                    authHeader={authHeader}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                )}
                {tab === 1 && (
                  <StatsTab url={url} authHeader={authHeader} />
                )}
                {tab === 2 && (
                  <TeamScheduleTab url={url} authHeader={authHeader} />
                )}
              </div>
            </>
          ) : (
            <div className="p-4 text-white/70">
              Come back after an admin links your account.
            </div>
          )}

          <button onClick={logout} className="m-4 text-white underline">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ url, authHeader, enqueueSnackbar }) {
  const [player, setPlayer] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Axios.get(url + "/players/me", authHeader)
      .then((res) => {
        setPlayer(res.data);
        const initial = {};
        EDITABLE_FIELDS.forEach(({ name }) => {
          initial[name] = res.data[name] ?? "";
        });
        setForm(initial);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    Axios.patch(url + "/players/me", form, authHeader)
      .then(() => enqueueSnackbar("Profile updated.", { variant: "success" }))
      .catch(() =>
        enqueueSnackbar("Could not update profile.", { variant: "error" })
      )
      .finally(() => setSaving(false));
  };

  if (!player) return <CircularProgress />;

  return (
    <form onSubmit={save} className="flex flex-col gap-4 max-w-[500px]">
      <div className="text-white font-bold text-lg">
        {player.firstname} {player.lastname}
      </div>
      <p className="text-white/60 text-xs -mt-2">
        Name, birthdate and division are managed by the league. Update your own
        details below.
      </p>
      {EDITABLE_FIELDS.map(({ name, label }) => (
        <TextField
          key={name}
          name={name}
          label={label}
          value={form[name] ?? ""}
          onChange={handleChange}
          size="small"
        />
      ))}
      <Button type="submit" variant="contained" disabled={saving}>
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </form>
  );
}

function StatsTab({ url, authHeader }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Axios.get(url + "/players/me/stats", authHeader)
      .then((res) => setStats(res.data))
      .catch(() => setStats({ error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return <CircularProgress />;
  if (stats.error)
    return <div className="text-white/70">Could not load stats.</div>;

  const at = stats.allTime;

  return (
    <div className="flex flex-col gap-6 text-white">
      <div>
        <h2 className="text-lg font-bold mb-2">All-Time</h2>
        {at ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Games" value={at.gamesPlayed} />
            <StatCard label="Points" value={at.pts} />
            <StatCard label="PPG" value={at.ppg} />
            <StatCard label="Record" value={`${at.win}-${at.lose}-${at.tie}`} />
            <StatCard label="3PT" value={at.threes} />
            <StatCard label="Championships" value={at.championships} />
          </div>
        ) : (
          <p className="text-white/60">No all-time stats yet.</p>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold mb-2">By Season</h2>
        {stats.seasons && stats.seasons.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-white/30">
                  <th className="p-2">Season</th>
                  <th className="p-2">GP</th>
                  <th className="p-2">Pts</th>
                  <th className="p-2">PPG</th>
                  <th className="p-2">W-L-T</th>
                </tr>
              </thead>
              <tbody>
                {stats.seasons.map((s) => (
                  <tr key={s._id} className="border-b border-white/10">
                    <td className="p-2">
                      {s.seasonID?.titleEn || s.seasonID?.title || "—"}
                    </td>
                    <td className="p-2">{s.gamesPlayed}</td>
                    <td className="p-2">{s.pts}</td>
                    <td className="p-2">{s.ppg}</td>
                    <td className="p-2">
                      {s.win}-{s.lose}-{s.tie}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-white/60">No season stats yet.</p>
        )}
      </div>

      {stats.awards && stats.awards.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-2">Awards</h2>
          <ul className="list-disc pl-5">
            {stats.awards.map((a) => (
              <li key={a._id}>
                {a.awardID?.titleEn || a.awardID?.title || "Award"}
                {a.seasonID?.titleEn ? ` — ${a.seasonID.titleEn}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TeamScheduleTab({ url, authHeader }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Axios.get(url + "/players/me/stats", authHeader)
      .then((res) => setStats(res.data))
      .catch(() => setStats({ error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return <CircularProgress />;
  if (stats.error)
    return <div className="text-white/70">Could not load team info.</div>;

  const games = (stats.games || [])
    .filter((g) => g.gameID)
    .sort(
      (a, b) =>
        new Date(b.gameID.date || b.gameID.creationDate || 0) -
        new Date(a.gameID.date || a.gameID.creationDate || 0)
    );

  return (
    <div className="flex flex-col gap-6 text-white">
      <div>
        <h2 className="text-lg font-bold mb-2">My Teams</h2>
        {stats.teams && stats.teams.length ? (
          <ul className="flex flex-col gap-2">
            {stats.teams.map((t) => (
              <li
                key={t._id}
                className="flex justify-between border-b border-white/10 py-2"
              >
                <span>{t.teamID?.name || t.teamID?.titleEn || "Team"}</span>
                <span className="text-white/60">
                  {t.seasonID?.titleEn || t.seasonID?.title || ""}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-white/60">No team assignments yet.</p>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold mb-2">Recent Games</h2>
        {games.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-white/30">
                  <th className="p-2">Date</th>
                  <th className="p-2">Team</th>
                  <th className="p-2">Pts</th>
                  <th className="p-2">Result</th>
                </tr>
              </thead>
              <tbody>
                {games.slice(0, 20).map((g) => (
                  <tr key={g._id} className="border-b border-white/10">
                    <td className="p-2">
                      {g.gameID?.date
                        ? new Date(g.gameID.date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="p-2">{g.teamID?.name || "—"}</td>
                    <td className="p-2">{g.pts}</td>
                    <td className="p-2">
                      {g.win === true ? "W" : g.win === false ? "L" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-white/60">No games recorded yet.</p>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white/10 rounded-lg p-3 text-center">
      <div className="text-2xl font-extrabold">{value ?? 0}</div>
      <div className="text-xs uppercase text-white/60">{label}</div>
    </div>
  );
}

export default Account;
